// src/Component/QuickServices/LocationPickerModal.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { parseLatLng } from "./geo";

const OLA_API_KEY = process.env.REACT_APP_OLA_API_KEY;
const DEBOUNCE_MS = 320;

// ── Ola Places autocomplete ───────────────────────────────────────────────────
async function fetchSuggestions(query) {
  if (!query.trim() || query.length < 3) return [];
  try {
    const res = await fetch(
      `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(query)}&api_key=${OLA_API_KEY}`
    );
    const data = await res.json();
    return data?.predictions ?? [];
  } catch {
    return [];
  }
}

// ── Ola reverse geocode ───────────────────────────────────────────────────────
// Exported so the crane modal can reuse it:
//   import LocationPickerModal, { reverseGeocode } from "./LocationPickerModal";
export async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://api.olamaps.io/places/v1/reverse-geocode?latlng=${lat},${lng}&api_key=${OLA_API_KEY}`
    );
    const data = await res.json();
    return data?.results?.[0]?.formatted_address ?? "Unknown address";
  } catch {
    return "Unknown address";
  }
}

// ── Ola place details (place_id → lat/lng) ────────────────────────────────────
async function fetchPlaceDetails(placeId) {
  try {
    const res = await fetch(
      `https://api.olamaps.io/places/v1/details?place_id=${placeId}&api_key=${OLA_API_KEY}`
    );
    const data = await res.json();
    const loc = data?.result?.geometry?.location;
    return loc ? { lat: loc.lat, lng: loc.lng } : null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Props:
//   field           "pickup" | "drop"   (controls marker colour + default title)
//   title           optional string overriding the header title
//   initialAddress  text OR pasted "lat, lng" to start from
//   onConfirm       ({ address, coords }) — coords may be null if unresolved
//   onClose         () => void
export default function LocationPickerModal({ field, title, initialAddress, onConfirm, onClose }) {
  const mapRef         = useRef(null);
  const markerRef      = useRef(null);
  const mapInstanceRef = useRef(null);
  const debounceTimer  = useRef(null);

  const [query,       setQuery]       = useState(initialAddress ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg,    setShowSugg]    = useState(false);
  const [address,     setAddress]     = useState(initialAddress ?? "");
  const [coords,      setCoords]      = useState(null);   // { lat, lng }
  const [mapReady,    setMapReady]    = useState(false);
  const [loading,     setLoading]     = useState(false);

  const isPickup    = field === "pickup";
  const markerColor = isPickup ? "#3b82f6" : "#10b981";   // blue / green

  // ── Move marker + reverse geocode ────────────────────────────────────────
  const moveMarkerTo = useCallback(async (lat, lng) => {
    setCoords({ lat, lng });
    if (markerRef.current) markerRef.current.setLngLat([lng, lat]);
    if (mapInstanceRef.current) mapInstanceRef.current.setCenter([lng, lat]);
    setLoading(true);
    const addr = await reverseGeocode(lat, lng);
    setAddress(addr);
    setQuery(addr);
    setLoading(false);
  }, []);

  // ── Init Ola map ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!mapRef.current) return;
      const { OlaMaps } = await import("olamaps-web-sdk");
      if (cancelled || !mapRef.current) return;
      const olaMaps = new OlaMaps({ apiKey: OLA_API_KEY });

      // 1) Work out a starting point from what we were given:
      //    pasted "lat, lng"  →  use directly
      //    address text       →  resolve via autocomplete + place details
      let start = null;
      const pastedCoords = parseLatLng(initialAddress);
      if (pastedCoords) {
        start = pastedCoords;
      } else if (initialAddress?.trim()) {
        const preds = await fetchSuggestions(initialAddress);
        if (preds[0]?.place_id) start = await fetchPlaceDetails(preds[0].place_id);
      }
      if (cancelled || !mapRef.current) return;

      // Default centre: Gurugram
      const center = start ? [start.lng, start.lat] : [77.0679, 28.4354];

      const myMap = olaMaps.init({
        style: "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
        container: mapRef.current,
        center,
        zoom: 15,
      });

      myMap.addControl(
        olaMaps.addNavigationControls({ showCompass: true, showZoom: true, visualizePitch: false })
      );

      // Draggable marker
      const marker = olaMaps
        .addMarker({
          color: markerColor,
          draggable: true,
          anchor: "bottom",
          offset: [0, 6],
        })
        .setLngLat(center)
        .addTo(myMap);

      marker.on("dragend", async () => {
        const { lat, lng } = marker.getLngLat();
        setCoords({ lat, lng });
        setLoading(true);
        const addr = await reverseGeocode(lat, lng);
        setAddress(addr);
        setQuery(addr);
        setLoading(false);
      });

      markerRef.current      = marker;
      mapInstanceRef.current = myMap;

      if (start) {
        if (pastedCoords) {
          // Sets coords AND replaces the pasted numbers with a readable address
          moveMarkerTo(start.lat, start.lng);
        } else {
          setCoords(start); // pin matches the text the user sees
        }
      } else if (!initialAddress?.trim()) {
        // 2) No address at all → use device GPS, and reverse-geocode it so the
        //    address text and coordinates always agree.
        navigator.geolocation?.getCurrentPosition(({ coords: c }) => {
          moveMarkerTo(c.latitude, c.longitude);
        });
      }
      // 3) Address given but not resolvable → leave coords null.
      //    The user must drag the pin (dragend sets both coords and address).

      setMapReady(true);
    };

    const t = setTimeout(load, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
      try { mapInstanceRef.current?.remove?.(); } catch { /* ignore */ }
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Autocomplete debounce (also handles pasted coordinates) ──────────────
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setAddress(val);           // keep input in sync
    clearTimeout(debounceTimer.current);
    if (val.length < 3) { setSuggestions([]); setShowSugg(false); return; }
    debounceTimer.current = setTimeout(async () => {
      const c = parseLatLng(val);
      if (c) {
        setSuggestions([]);
        setShowSugg(false);
        await moveMarkerTo(c.lat, c.lng);   // moves pin, fills the address
        return;
      }
      const results = await fetchSuggestions(val);
      setSuggestions(results);
      setShowSugg(results.length > 0);
    }, DEBOUNCE_MS);
  };

  const handleSuggestionClick = async (suggestion) => {
    const label = suggestion.description ?? suggestion.structured_formatting?.main_text ?? "";
    setQuery(label);
    setAddress(label);
    setShowSugg(false);
    setSuggestions([]);

    // Resolve place_id → lat/lng
    if (suggestion.place_id) {
      setLoading(true);
      const loc = await fetchPlaceDetails(suggestion.place_id);
      setLoading(false);
      if (loc) {
        setCoords(loc);
        if (mapInstanceRef.current) mapInstanceRef.current.setCenter([loc.lng, loc.lat]);
        if (markerRef.current) markerRef.current.setLngLat([loc.lng, loc.lat]);
      }
    }
  };

  const handleConfirm = () => {
    if (!address.trim()) return;
    onConfirm({ address, coords });   // coords may be null if user only typed
    onClose();
  };

  return (
    <div className="lpm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lpm-modal">

        {/* Header */}
        <div className="lpm-header">
          <div className="lpm-header-left">
            <div className={`lpm-dot ${isPickup ? "lpm-dot--pickup" : "lpm-dot--drop"}`} />
            <span className="lpm-title">
              {title || (isPickup ? "Set pickup location" : "Set drop / destination")}
            </span>
          </div>
          <button className="lpm-close" onClick={onClose} aria-label="Close map picker">
            <XIcon />
          </button>
        </div>

        {/* Search bar */}
        <div className="lpm-search-wrap">
          <span className="lpm-search-icon"><SearchIcon /></span>
          <input
            className="lpm-search-input"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => suggestions.length > 0 && setShowSugg(true)}
            placeholder="Search a place, or paste coordinates (28.4595, 77.0266)"
            autoFocus
            autoComplete="off"
          />
          {query && (
            <button
              className="lpm-search-clear"
              onClick={() => { setQuery(""); setAddress(""); setSuggestions([]); setShowSugg(false); }}
            >
              <XSmIcon />
            </button>
          )}

          {/* Suggestions dropdown */}
          {showSugg && (
            <ul className="lpm-suggestions">
              {suggestions.map((s, i) => (
                <li
                  key={s.place_id ?? i}
                  className="lpm-suggestion-item"
                  onMouseDown={() => handleSuggestionClick(s)}
                >
                  <span className="lpm-sugg-icon"><PinIcon /></span>
                  <div className="lpm-sugg-text">
                    <span className="lpm-sugg-main">
                      {s.structured_formatting?.main_text ?? s.description}
                    </span>
                    {s.structured_formatting?.secondary_text && (
                      <span className="lpm-sugg-sub">{s.structured_formatting.secondary_text}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Map */}
        <div className="lpm-map-wrap">
          <div ref={mapRef} className="lpm-map" />
          {!mapReady && (
            <div className="lpm-map-loading">
              <span className="lpm-spinner" />
              <span>Loading map…</span>
            </div>
          )}
          <div className="lpm-hint">Drag the pin to adjust</div>
        </div>

        {/* Footer — selected address + confirm */}
        <div className="lpm-footer">
          <div className="lpm-footer-addr">
            <span className={`lpm-dot lpm-dot--sm ${isPickup ? "lpm-dot--pickup" : "lpm-dot--drop"}`} />
            <span className="lpm-footer-text">
              {loading ? "Fetching address…" : (address || "No location selected")}
            </span>
          </div>
          <button
            className="wd-btn wd-btn-primary lpm-confirm-btn"
            onClick={handleConfirm}
            disabled={!address.trim() || loading}
          >
            <CheckIcon /> Confirm location
          </button>
        </div>
      </div>

      <style>{LPM_CSS}</style>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
function XIcon()    { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function XSmIcon()  { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function SearchIcon(){ return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function PinIcon()  { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function CheckIcon(){ return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>; }

// ── CSS ───────────────────────────────────────────────────────────────────────
const LPM_CSS = `
.lpm-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.82);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  z-index: 1100;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: lpm-fade 0.15s ease;
}
@keyframes lpm-fade { from { opacity: 0; } to { opacity: 1; } }

.lpm-modal {
  background: #161e2e;
  border: 1px solid #243044;
  border-radius: 14px;
  width: 100%;
  max-width: 560px;
  height: min(680px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 32px 96px rgba(0,0,0,0.7);
  animation: lpm-up 0.18s ease;
}
@keyframes lpm-up {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Header */
.lpm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #1e2c42;
  background: #111827;
  flex-shrink: 0;
}
.lpm-header-left { display: flex; align-items: center; gap: 10px; }
.lpm-title { font-size: 14px; font-weight: 600; color: #f1f5f9; }
.lpm-close {
  width: 28px; height: 28px; border-radius: 6px;
  border: none; background: transparent; cursor: pointer;
  color: #64748b; display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.lpm-close:hover { background: #1e2c42; color: #cbd5e1; }

/* Dot indicator */
.lpm-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.lpm-dot--sm { width: 8px; height: 8px; }
.lpm-dot--pickup { background: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.2); }
.lpm-dot--drop   { background: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.2); }

/* Search */
.lpm-search-wrap {
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid #1e2c42;
  background: #111827;
  flex-shrink: 0;
}
.lpm-search-icon {
  position: absolute; left: 28px; top: 50%; transform: translateY(-50%);
  color: #475569; pointer-events: none;
  display: flex; align-items: center;
}
.lpm-search-input {
  width: 100%; height: 38px;
  padding: 0 36px 0 36px;
  background: #0f1623;
  border: 1px solid #243044;
  border-radius: 8px;
  font-size: 13px; color: #e2e8f0;
  outline: none; box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.lpm-search-input::placeholder { color: #374f6b; }
.lpm-search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.12);
  background: #0d1520;
}
.lpm-search-clear {
  position: absolute; right: 28px; top: 50%; transform: translateY(-50%);
  width: 20px; height: 20px; border-radius: 50%;
  border: none; background: #243044; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
}
.lpm-search-clear:hover { background: #2d3f58; color: #e2e8f0; }

/* Suggestions dropdown */
.lpm-suggestions {
  position: absolute; top: calc(100% - 4px); left: 16px; right: 16px;
  background: #0f1e35;
  border: 1px solid #243044;
  border-radius: 9px;
  list-style: none; margin: 0; padding: 4px 0;
  z-index: 50;
  max-height: 220px; overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  scrollbar-width: thin; scrollbar-color: #243044 transparent;
}
.lpm-suggestion-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 9px 14px; cursor: pointer;
  transition: background 0.12s;
}
.lpm-suggestion-item:hover { background: #162032; }
.lpm-sugg-icon { color: #475569; flex-shrink: 0; margin-top: 2px; }
.lpm-sugg-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.lpm-sugg-main { font-size: 13px; color: #e2e8f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lpm-sugg-sub  { font-size: 11px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Map */
.lpm-map-wrap {
  flex: 1;
  position: relative;
  overflow: hidden;
}
.lpm-map { width: 100%; height: 100%; }
.lpm-map-loading {
  position: absolute; inset: 0;
  background: #0f1623;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 10px; color: #64748b; font-size: 13px;
}
.lpm-spinner {
  width: 20px; height: 20px;
  border: 2px solid rgba(255,255,255,0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: lpm-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes lpm-spin { to { transform: rotate(360deg); } }
.lpm-hint {
  position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.65);
  color: #94a3b8; font-size: 11px;
  padding: 4px 10px; border-radius: 20px;
  white-space: nowrap; pointer-events: none;
}

/* Footer */
.lpm-footer {
  padding: 14px 20px;
  border-top: 1px solid #1e2c42;
  background: #111827;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.lpm-footer-addr {
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 0;
}
.lpm-footer-text {
  font-size: 12px; color: #94a3b8;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.lpm-confirm-btn {
  flex-shrink: 0;
  display: flex; align-items: center; gap: 6px;
}

@media (max-width: 600px) {
  .lpm-modal { height: 100dvh; max-width: 100%; border-radius: 0; }
  .lpm-overlay { padding: 0; }
  .lpm-footer { flex-direction: column; align-items: stretch; }
  .lpm-confirm-btn { justify-content: center; }
}
`;